/** Alert categorization — single source of truth shared by mobile + server. */

export type AlertCategory = 'all' | 'speed' | 'geofence' | 'maint' | 'sos' | 'environment' | 'other';

/** DB-level mapping. 'all' is UI-only; never appears as a server-side value. */
export const EVENT_CATEGORY: Record<string, Exclude<AlertCategory, 'all'>> = {
  deviceOverspeed: 'speed',
  speedInGeofence: 'speed',
  harshAcceleration: 'speed',
  harshBraking: 'speed',
  // Two spellings, one bucket (TASK-338): rule_events from the evaluator and
  // the GPSgate importer carry the condition type `geofenceEntry`; Traccar's
  // tc_events and legacy rows carry `geofenceEnter`.
  geofenceEnter: 'geofence',
  geofenceEntry: 'geofence',
  geofenceExit: 'geofence',
  enterHighRiskArea: 'geofence',
  maintenanceDueSoon: 'maint',
  maintenanceOverdue: 'maint',
  sosPressed: 'sos',
  staffSos: 'sos',
  staffSosUnacknowledged: 'sos',
  checkInMissed: 'sos',
  powerCut: 'sos',
  trackerTamper: 'sos',
  linkedTrackerOffline: 'sos',
  weatherAqi: 'environment',
  weatherDust: 'environment',
  weatherWind: 'environment',
  weatherTemp: 'environment',
  staffWeatherAqi: 'environment',
  staffWeatherDust: 'environment',
  staffWeatherWind: 'environment',
  staffWeatherTemp: 'environment',
};

/** Unknown event types fall through to 'other' — the "All" tab always shows them. */
export function categorizeEvent(eventType: string): Exclude<AlertCategory, 'all'> {
  return EVENT_CATEGORY[eventType] ?? 'other';
}

/** Hex color per category. Mirrors STATUS_COLOR convention. */
export const CATEGORY_COLOR: Record<AlertCategory, string> = {
  all: '#64748b',
  speed: '#ef4444',
  geofence: '#2563eb',
  maint: '#f59e0b',
  sos: '#dc2626',
  environment: '#0d9488',
  other: '#94a3b8',
};

/** Utility for server-side filtering — returns all event_type strings in a category. */
export function eventTypesForCategory(category: Exclude<AlertCategory, 'all'>): string[] {
  return Object.entries(EVENT_CATEGORY)
    .filter(([, cat]) => cat === category)
    .map(([type]) => type);
}
